import { useState, useEffect } from 'react';
import { useAuthStore } from '../../stores';
import { api } from '../../utils/api';
import AdminLayout from './AdminLayout';
import JsonEditor from './JsonEditor';
import AdminEventLayers from './AdminEventLayers';

export default function AdminEvents() {
  const { token } = useAuthStore();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [managingLayersEvent, setManagingLayersEvent] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    logo_url: '',
    start_date: '',
    end_date: '',
    is_active: true,
    metadata: {}
  });

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      setLoading(true);
      const response = await api.getAllEvents({ limit: 100 });
      setEvents(response.events || []);
    } catch (error) {
      console.error('Error loading events:', error);
      alert('Σφάλμα φόρτωσης events');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove accents
      .replace(/[^\w\s-]/g, '') // Remove special chars
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens
      .trim();
  };

  const handleNameChange = (name) => {
    setFormData({
      ...formData,
      name,
      slug: generateSlug(name)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.slug || !formData.start_date || !formData.end_date) {
      alert('Παρακαλώ συμπληρώστε όλα τα απαιτούμενα πεδία');
      return;
    }

    if (new Date(formData.end_date) < new Date(formData.start_date)) {
      alert('Η ημερομηνία λήξης πρέπει να είναι μετά την ημερομηνία έναρξης');
      return;
    }

    try {
      const data = {
        ...formData,
        metadata: typeof formData.metadata === 'string' 
          ? JSON.parse(formData.metadata) 
          : formData.metadata
      };

      if (editingEvent) {
        // Update existing event
        await api.adminUpdateEvent(token, editingEvent.id, data);
        alert('Το event ενημερώθηκε επιτυχώς!');
      } else {
        // Create new event
        await api.adminCreateEvent(token, data);
        alert('Το event δημιουργήθηκε επιτυχώς!');
      }
      resetForm();
      loadEvents();
    } catch (error) {
      console.error('Error saving event:', error);
      alert('Σφάλμα αποθήκευσης event: ' + error.message);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      description: '',
      logo_url: '',
      start_date: '',
      end_date: '',
      is_active: true,
      metadata: {}
    });
    setEditingEvent(null);
    setShowForm(false);
    setManagingLayersEvent(null);
  };

  const handleEdit = (event) => {
    setFormData({
      name: event.name,
      slug: event.slug,
      description: event.description || '',
      logo_url: event.logo_url || '',
      start_date: event.start_date.split('T')[0],
      end_date: event.end_date.split('T')[0],
      is_active: event.is_active,
      metadata: JSON.stringify(event.metadata || {}, null, 2)
    });
    setEditingEvent(event);
    setShowForm(true);
    setManagingLayersEvent(null);
  };

  const handleManageLayers = (event) => {
    setManagingLayersEvent(event);
    setShowForm(false);
    setEditingEvent(null);
  };

  const handleDelete = async (event) => {
    if (!confirm(`Είστε σίγουροι ότι θέλετε να διαγράψετε το event "${event.name}";`)) {
      return;
    }

    try {
      await api.adminDeleteEvent(token, event.id);
      alert('Το event διαγράφηκε επιτυχώς!');
      loadEvents();
    } catch (error) {
      console.error('Error deleting event:', error);
      alert('Σφάλμα διαγραφής event: ' + error.message);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('el-GR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <AdminLayout>
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Διαχείριση Events</h1>
          <p className="text-gray-600 mt-2">
            Δημιουργία και διαχείριση εκδηλώσεων και των επιπέδων χάρτη τους
          </p>
        </div>
        <button
          onClick={() => {
            setShowForm(!showForm);
            setManagingLayersEvent(null);
            if (!showForm) resetForm();
          }}
          className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
        >
          <span className="text-xl">+</span>
          Νέο Event
        </button>
      </div>

      {/* Layer Management View */}
      {managingLayersEvent && (
        <div className="mb-6">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={() => setManagingLayersEvent(null)}
              className="text-blue-600 hover:text-blue-800 flex items-center gap-2"
            >
              ← Επιστροφή στα Events
            </button>
            <h2 className="text-2xl font-bold">
              Διαχείριση Layers - {managingLayersEvent.name}
            </h2>
          </div>
          <AdminEventLayers 
            event={managingLayersEvent} 
            onBack={() => setManagingLayersEvent(null)}
          />
        </div>
      )}

      {/* Event Form */}
      {showForm && !managingLayersEvent && (
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-xl font-bold mb-4">
            {editingEvent ? 'Επεξεργασία Event' : 'Νέο Event'}
          </h2>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Όνομα Event *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="π.χ. Μαραθώνιος Αθήνας 2024"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Slug (URL) *
                </label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  placeholder="marathonios-athinas-2024"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ημερομηνία Έναρξης *
                </label>
                <input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ημερομηνία Λήξης *
                </label>
                <input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  URL Logo
                </label>
                <input
                  type="url"
                  value={formData.logo_url}
                  onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                  placeholder="https://example.com/logo.png"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Περιγραφή
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={5}
                placeholder="Περιγράψτε το event..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <JsonEditor
              value={formData.metadata}
              onChange={(value) => setFormData({ ...formData, metadata: value })}
              placeholder='{"distance": "42.195km", "participants": 50000}'
            />

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="is_active" className="ml-2 text-sm font-medium text-gray-700">
                Ενεργό Event (εμφανίζεται στην εφαρμογή)
              </label>
            </div>

            <div className="flex gap-4">
              <button
                type="submit"
                className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition"
              >
                {editingEvent ? 'Ενημέρωση' : 'Δημιουργία'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="bg-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-400 transition"
              >
                Ακύρωση
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Events List */}
      {!managingLayersEvent && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b">
            <h2 className="text-xl font-bold">Λίστα Events ({events.length})</h2>
          </div>
        
        {loading ? (
          <div className="p-12 text-center text-gray-500">
            Φόρτωση...
          </div>
        ) : events.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            Δεν υπάρχουν events. Δημιουργήστε το πρώτο event!
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Event</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ημερομηνίες</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Κατάσταση</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Δημιουργία</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ενέργειες</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {events.map((event) => (
                  <tr key={event.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        {event.logo_url && (
                          <img 
                            src={event.logo_url} 
                            alt={event.name}
                            className="w-10 h-10 rounded mr-3 object-cover"
                          />
                        )}
                        <div>
                          <div className="font-medium text-gray-900">{event.name}</div>
                          <div className="text-sm text-gray-500">{event.slug}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      <div>{formatDate(event.start_date)}</div>
                      <div className="text-xs text-gray-400">έως {formatDate(event.end_date)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {event.is_active ? (
                        <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                          ✓ Ενεργό
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">
                          ✕ Ανενεργό
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(event.created_at).toLocaleDateString('el-GR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleManageLayers(event)}
                        className="text-purple-600 hover:text-purple-900 mr-4"
                        title="Διαχείριση Layers"
                      >
                        🗺️ Layers
                      </button>
                      <button
                        onClick={() => handleEdit(event)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                      >
                        Επεξεργασία
                      </button>
                      <button
                        onClick={() => handleDelete(event)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Διαγραφή
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </div>
      )}
    </AdminLayout>
  );
}

